export type WhitepaperLearnItem = {
  title: string
  description: string
}

export type WhitepaperTitleParts = {
  prefix: string
  accent: string
  suffix: string
}

export type Whitepaper = {
  slug: string
  title: string
  shortDescription: string
  eyebrow: string
  titleParts: WhitepaperTitleParts
  heroDescription: string
  learnIntro: string
  learnItems: WhitepaperLearnItem[]
  coverImageSrc: string
  coverImageAlt: string
  heroBgSrc: string
  modalHeroSrc: string
  featured: boolean
  publishedAt: string
}

const WHITEPAPERS: Whitepaper[] = [
  {
    slug: 'great-recomposition-2026',
    title:
      'The Great Recomposition: How AI is Changing the Workforce and Why Traditional Outplacement Has Failed',
    shortDescription:
      'How generative AI is reshaping the labor market and why traditional outplacement no longer fits.',
    eyebrow: 'H1 2026 Research Report',
    titleParts: {
      prefix: 'The ',
      accent: 'Great Recomposition',
      suffix: ': How AI is Changing the Workforce and Why Traditional Outplacement Has Failed',
    },
    heroDescription:
      'Uncover findings from a survey of 5,000 unemployed Americans, and learn how the rapid adoption of generative AI is creating a structural mismatch in the labor market that traditional corporate outplacement can no longer address.',
    learnIntro:
      'Based on a synthesis of macroeconomic data and the rapid adoption of generative AI, this report outlines the structural mismatch in the labor market and the failure of traditional corporate outplacement to address the velocity of modern workforce change.',
    learnItems: [
      {
        title: 'The Entry-Level Squeeze',
        description:
          'Learn why early-career hiring has declined by 16% as organizations pivot toward experienced talent capable of managing AI-integrated workflows.',
      },
      {
        title: 'The Educational ROI Re-Evaluation',
        description:
          'Discover why 60% of students are now prioritizing vocational programs over four-year colleges as the perceived value of traditional degrees faces unprecedented scrutiny.',
      },
      {
        title: 'The Structural Gap & Velocity Shock',
        description:
          'Understand the disconnect between 7.4 million unfilled job openings and the reality that AI agents can now complete professional tasks in minutes that previously required days of human labor.',
      },
      {
        title: 'Strategic Workforce Reinvention',
        description:
          'See how organizations are moving beyond "career transition" to "workforce reinvention" by deploying AI-native career intelligence to reskill talent at scale.',
      },
    ],
    coverImageSrc: '/assets/whitepapers/great-recomposition-2026/cover.png',
    coverImageAlt: 'The Great Recomposition whitepaper cover',
    heroBgSrc: '/assets/whitepapers/great-recomposition-2026/hero-bg.jpg',
    modalHeroSrc: '/assets/whitepapers/great-recomposition-2026/hero-bg.jpg',
    featured: true,
    publishedAt: '2026-06-01',
  },
  {
    slug: 'empathy-imperative-2026',
    title: 'The Empathy Imperative Amidst Global Labor Market Transformation',
    shortDescription:
      'Survey insights on compassionate, AI-driven outplacement as a strategic financial imperative.',
    eyebrow: 'H1 2026 Research Report',
    titleParts: {
      prefix: 'The ',
      accent: 'Empathy Imperative ',
      suffix: 'Amidst Global Labor Market Transformation',
    },
    heroDescription:
      'Uncover findings from a survey of 5,000 unemployed Americans, and learn why transitioning to AI-driven, compassionate outplacement is now a strategic financial imperative.',
    learnIntro:
      'Based on a double-opt in survey of 5,000 unemployed Americans, this report synthesizes macroeconomic data with the human experience of job loss to outline the real cost of the modern "empathy deficit".',
    learnItems: [
      {
        title: 'The True Cost of Reputational Aftershock',
        description:
          'Learn why 69% of employees are willing to share negative layoff experiences online, and how this transparency directly impacts future talent acquisition.',
      },
      {
        title: 'Combatting Layoff Survivor Syndrome',
        description:
          'Understand the internal fallout that causes a 20% decline in productivity and makes surviving employees 2.5 times more likely to leave voluntarily.',
      },
      {
        title: 'The ROI of Empathy',
        description:
          'Discover how high-empathy organizations achieve 56% higher revenue growth and avoid their share of the $180 billion lost annually to attrition costs.',
      },
      {
        title: 'The Digital Evolution of Outplacement',
        description:
          'See how AI-driven platforms provide scalable, high-touch transition support that reduces termination-related litigation risk by 80%.',
      },
    ],
    coverImageSrc: '/assets/whitepapers/empathy-imperative-2026/cover.png',
    coverImageAlt: 'The Empathy Imperative whitepaper cover',
    heroBgSrc: '/assets/whitepapers/empathy-imperative-2026/hero-bg.jpg',
    modalHeroSrc: '/assets/whitepapers/empathy-imperative-2026/cover.png',
    featured: false,
    publishedAt: '2026-01-01',
  },
]

export function getWhitepaper(slug: string): Whitepaper | undefined {
  return WHITEPAPERS.find((paper) => paper.slug === slug)
}

export function getFeaturedWhitepaper(): Whitepaper {
  const featured = WHITEPAPERS.find((paper) => paper.featured)
  if (!featured) {
    throw new Error('No featured whitepaper configured')
  }
  return featured
}

export function listWhitepapers(): Whitepaper[] {
  return [...WHITEPAPERS].sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
  )
}

export function getWhitepaperLandingPath(slug: string): string {
  return `/resources/whitepapers/${slug}`
}
